import React, { useState, useEffect } from 'react';
import { 
  Plus, Search, Filter, Edit2, Trash2, Copy, Eye, EyeOff, 
  Check, X, Package, IndianRupee,
  Grid, List, Loader2
} from 'lucide-react';
import { authFetch } from '../services/api';
import ImageUploader from '../components/ImageUploader';

const ProductAdminPanel = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [categories, setCategories] = useState([]);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchQuery, categoryFilter, activeFilter]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: 1,
        limit: 1000
      });

      if (debouncedSearchQuery) params.append('search', debouncedSearchQuery);
      if (categoryFilter) params.append('category', categoryFilter);
      if (activeFilter) params.append('is_active', activeFilter);

      const response = await authFetch(`/api/admin/products?${params}`);
      const data = await response.json();

      if (data.success) {
        setProducts(data.products);
      }
    } catch (err) {
      console.error('Error fetching products:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await authFetch('/api/admin/categories');
      const data = await response.json();
      if (data.success) {
        setCategories(data.categories);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const handleDelete = async (productId, hardDelete = false) => {
    if (!window.confirm(hardDelete ? 
      'Permanently delete this product? This cannot be undone!' : 
      'Deactivate this product?')) {
      return;
    }

    try {
      const response = await authFetch(
        `/api/admin/products/${productId}?hard_delete=${hardDelete}`,
        { method: 'DELETE' }
      );
      const data = await response.json();

      if (data.success) {
        alert(data.message);
        fetchProducts();
      } else {
        alert(data.error);
      }
    } catch (err) {
      console.error('Error deleting product:', err);
      alert('Failed to delete product');
    }
  };

  const handleDuplicate = async (productId) => {
    try {
      const response = await authFetch(
        `/api/admin/products/${productId}/duplicate`,
        { method: 'POST' }
      );
      const data = await response.json();

      if (data.success) {
        alert('Product duplicated successfully!');
        fetchProducts();
      } else {
        alert(data.error);
      }
    } catch (err) {
      console.error('Error duplicating product:', err);
      alert('Failed to duplicate product');
    }
  };

  const handleBulkUpdate = async (updates) => {
    if (selectedProducts.length === 0) {
      alert('No products selected');
      return;
    }

    try {
      const response = await authFetch('/api/admin/products/bulk', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_ids: selectedProducts,
          updates
        })
      });
      const data = await response.json();

      if (data.success) {
        alert(data.message);
        setSelectedProducts([]);
        fetchProducts();
      } else {
        alert(data.error);
      }
    } catch (err) {
      console.error('Error bulk updating:', err);
      alert('Failed to update products');
    }
  };

  const toggleProductSelection = (productId) => {
    setSelectedProducts(prev => 
      prev.includes(productId)
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const selectAllProducts = () => {
    if (selectedProducts.length === products.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(products.map(p => p.id));
    }
  };

  const ProductCard = ({ product }) => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
      <div className="relative">
        <input
          type="checkbox"
          checked={selectedProducts.includes(product.id)}
          onChange={() => toggleProductSelection(product.id)}
          className="absolute top-3 left-3 w-5 h-5 rounded cursor-pointer z-10"
        />
        <div className="h-52 bg-white p-4 flex items-center justify-center overflow-hidden">
          {product.image_url ? (
            <img
              src={product.image_url}
              alt={product.name}
              className="w-full h-full object-contain drop-shadow-md"
            />
          ) : (
            <Package className="w-16 h-16 text-gray-300" />
          )}
        </div>
        {!product.is_active && (
          <div className="absolute top-3 right-3 bg-red-500 text-white px-2 py-1 rounded-full text-xs font-semibold">
            Inactive
          </div>
        )}
      </div>
      
      <div className="p-4">
        <h3 className="font-bold text-gray-900 text-sm mb-1 truncate">
          {product.name}
        </h3>
        <p className="text-xs text-gray-600 mb-1">{product.category}</p>
        {product.brand && (
          <p className="text-xs text-gray-500 mb-1">
            <span className="font-semibold">Brand:</span> {product.brand}
          </p>
        )}
        {product.model && (
          <p className="text-xs text-gray-500 mb-2 truncate">
            <span className="font-semibold">Model:</span> {product.model}
          </p>
        )}
        
        <div className="flex items-center mb-3">
          <span className="flex items-center gap-1 font-bold text-gray-900">
            <IndianRupee className="w-4 h-4" />
            {parseFloat(product.price).toLocaleString('en-IN')}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              setEditingProduct(product);
              setShowEditModal(true);
            }}
            className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-orange-50 text-orange-600 rounded-lg text-xs font-semibold hover:bg-orange-100"
          >
            <Edit2 className="w-3 h-3" />
            Edit
          </button>
          <button
            onClick={() => handleDuplicate(product.id)}
            className="px-3 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200"
          >
            <Copy className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleDelete(product.id)}
            className="px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );

  const ProductRow = ({ product }) => (
    <tr className="border-b border-gray-200 hover:bg-gray-50">
      <td className="py-3 px-4">
        <input
          type="checkbox"
          checked={selectedProducts.includes(product.id)}
          onChange={() => toggleProductSelection(product.id)}
          className="w-4 h-4 rounded cursor-pointer"
        />
      </td>
      <td className="py-3 px-4">
        <div className="flex items-center gap-3">
          <img
            src={product.image_url || '/images/logo.png'}
            alt={product.name}
            className="w-12 h-12 object-cover rounded"
          />
          <div>
            <p className="font-semibold text-gray-900 text-sm">{product.name}</p>
            {product.brand && (
              <p className="text-xs text-gray-500">Brand: {product.brand}</p>
            )}
            {product.model && (
              <p className="text-xs text-gray-500">Model: {product.model}</p>
            )}
          </div>
        </div>
      </td>
      <td className="py-3 px-4 text-sm text-gray-600">{product.category}</td>
      <td className="py-3 px-4">
        <span className="flex items-center gap-1 font-bold text-gray-900 text-sm">
          <IndianRupee className="w-3 h-3" />
          {parseFloat(product.price).toLocaleString('en-IN')}
        </span>
      </td>
      <td className="py-3 px-4">
        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${
          product.is_active 
            ? 'bg-green-100 text-green-800' 
            : 'bg-red-100 text-red-800'
        }`}>
          {product.is_active ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
          {product.is_active ? 'Active' : 'Inactive'}
        </span>
      </td>
      <td className="py-3 px-4">
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              setEditingProduct(product);
              setShowEditModal(true);
            }}
            className="p-2 bg-orange-50 text-orange-600 rounded-lg hover:bg-orange-100"
          >
            <Edit2 className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleDuplicate(product.id)}
            className="p-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200"
          >
            <Copy className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleDelete(product.id)}
            className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </td>
    </tr>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-black text-gray-900 mb-2">Product Management</h1>
          <p className="text-gray-600">Manage your product catalog</p>
        </div>

        {/* Toolbar */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-wrap items-center gap-3">
            
            {/* Search */}
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search products by name, brand, model..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
                {searchQuery && searchQuery !== debouncedSearchQuery && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-orange-500 animate-spin" />
                )}
                {debouncedSearchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Category Filter */}
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat.name} value={cat.name}>{cat.name}</option>
              ))}
            </select>

            {/* Status Filter */}
            <select
              value={activeFilter}
              onChange={(e) => setActiveFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
            >
              <option value="">All Status</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>

            {/* View Mode Toggle */}
            <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-lg">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded ${viewMode === 'grid' ? 'bg-white shadow' : ''}`}
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded ${viewMode === 'list' ? 'bg-white shadow' : ''}`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>

            {/* Add Product Button */}
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg font-semibold hover:bg-orange-700"
            >
              <Plus className="w-4 h-4" />
              Add Product
            </button>

            {/* Manage Categories Button */}
            <button
              onClick={() => setShowCategoryModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700"
            >
              <Filter className="w-4 h-4" />
              Manage Categories
            </button>
          </div>
        </div>

        {/* Bulk Actions */}
        {selectedProducts.length > 0 && (
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-6">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-orange-900">
                {selectedProducts.length} product(s) selected
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleBulkUpdate({ is_active: true })}
                  className="flex items-center gap-1 px-3 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700"
                >
                  <Eye className="w-4 h-4" />
                  Activate
                </button>
                <button
                  onClick={() => handleBulkUpdate({ is_active: false })}
                  className="flex items-center gap-1 px-3 py-2 bg-gray-600 text-white rounded-lg text-sm font-semibold hover:bg-gray-700"
                >
                  <EyeOff className="w-4 h-4" />
                  Deactivate
                </button>
                <button
                  onClick={() => setSelectedProducts([])}
                  className="px-3 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-50"
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Products Display */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-12 h-12 border-4 border-orange-600 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-600">Loading products...</p>
          </div>
        ) : products.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center">
            <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">No products found</h3>
            <p className="text-gray-600 mb-6">Get started by adding your first product</p>
            <button
              onClick={() => setShowAddModal(true)}
              className="inline-flex items-center gap-2 px-6 py-3 bg-orange-600 text-white rounded-lg font-semibold hover:bg-orange-700"
            >
              <Plus className="w-5 h-5" />
              Add Product
            </button>
          </div>
        ) : (
          <>
            {viewMode === 'grid' ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {products.map(product => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr className="border-b border-gray-200">
                      <th className="py-3 px-4 text-left">
                        <input
                          type="checkbox"
                          checked={selectedProducts.length === products.length}
                          onChange={selectAllProducts}
                          className="w-4 h-4 rounded cursor-pointer"
                        />
                      </th>
                      <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase">Product</th>
                      <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase">Category</th>
                      <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase">Price</th>
                      <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                      <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map(product => (
                      <ProductRow key={product.id} product={product} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* Add/Edit Product Modals would go here */}
        {/* For brevity, showing placeholder */}
        {(showAddModal || showEditModal) && (
          <ProductFormModal
            product={editingProduct}
            categories={categories}
            onClose={() => {
              setShowAddModal(false);
              setShowEditModal(false);
              setEditingProduct(null);
            }}
            onSuccess={() => {
              fetchProducts();
              setShowAddModal(false);
              setShowEditModal(false);
              setEditingProduct(null);
            }}
          />
        )}

        {/* Category Management Modal */}
        {showCategoryModal && (
          <CategoryManagementModal
            categories={categories}
            onClose={() => setShowCategoryModal(false)}
            onSuccess={() => {
              fetchCategories();
              setShowCategoryModal(false);
            }}
          />
        )}

      </div>
    </div>
  );
};

// Helper: delete an image from Supabase Storage via backend
const deleteStorageImage = async (imageUrl) => {
  if (!imageUrl || !imageUrl.includes('supabase')) return; // skip placeholder/external URLs
  try {
    const adminAuthService = (await import('../services/adminAuthService')).default;
    const session = adminAuthService.getCurrentSession();
    if (!session?.access_token) return;
    await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:4002'}/api/admin/delete-image`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      },
      body: JSON.stringify({ url: imageUrl })
    });
    console.log('🗑️ Old image deleted from storage:', imageUrl);
  } catch (err) {
    console.warn('⚠️ Could not delete old image (non-fatal):', err.message);
  }
};

// Product Form Modal Component
const ProductFormModal = ({ product, categories, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: product?.name || '',
    description: product?.description || '',
    price: product?.price || '',
    category: product?.category || '',
    brand: product?.brand || '',
    model: product?.model || '',
    image_url: product?.image_url || '',
    is_active: product?.is_active ?? true
  });
  const [loading, setLoading] = useState(false);
  // Track the image URL that was on the product when the modal opened
  // so we can delete it from storage if the user replaces or removes it
  const originalImageUrl = product?.image_url || '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    console.log('📝 Submitting product with data:', formData);

    try {
      const url = product 
        ? `/api/admin/products/${product.id}`
        : `/api/admin/products`;
      
      const method = product ? 'PUT' : 'POST';

      const response = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      console.log('📨 Server response:', data);

      if (data.success) {
        alert(data.message);
        onSuccess();
      } else {
        alert(data.error);
      }
    } catch (err) {
      console.error('Error saving product:', err);
      alert('Failed to save product');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-black text-gray-900">
            {product ? 'Edit Product' : 'Add New Product'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Product Name *
            </label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Price (₹) *
            </label>
            <input
              type="number"
              required
              step="0.01"
              value={formData.price}
              onChange={(e) => setFormData({ ...formData, price: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Brand
              </label>
              <input
                type="text"
                value={formData.brand}
                onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                placeholder="e.g., Samsung, Whirlpool, Haier"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Model / Details
              </label>
              <input
                type="text"
                value={formData.model}
                onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                placeholder="e.g., RT40H28W, 7.5 kg"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Category *
            </label>
            <select
              required
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
            >
              <option value="">Select Category</option>
              {categories.map((cat, idx) => (
                <option key={cat.id || idx} value={cat.name}>{cat.name}</option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              {categories.length === 0 ? (
                <span className="text-orange-600 font-semibold">
                  No categories yet. Click "Manage Categories" to add one.
                </span>
              ) : (
                'Select from existing categories'
              )}
            </p>
          </div>

          <div>
            <ImageUploader
              currentImageUrl={formData.image_url}
              onImageUploaded={(url, path) => {
                // Delete the old image from storage before switching to the new one
                const oldUrl = formData.image_url || originalImageUrl;
                if (oldUrl && oldUrl !== url) {
                  deleteStorageImage(oldUrl);
                }
                setFormData({ ...formData, image_url: url });
              }}
              onImageRemoved={() => {
                // Delete from storage when the user explicitly removes the image
                const oldUrl = formData.image_url || originalImageUrl;
                if (oldUrl) {
                  deleteStorageImage(oldUrl);
                }
                setFormData({ ...formData, image_url: '' });
              }}
              folder="products"
              label="Product Image"
              required={false}
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              className="w-4 h-4 rounded"
            />
            <label htmlFor="is_active" className="text-sm font-semibold text-gray-700">
              Active (visible to customers)
            </label>
          </div>

          <div className="flex items-center gap-3 pt-4">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-3 bg-orange-600 text-white rounded-lg font-semibold hover:bg-orange-700 disabled:opacity-50"
            >
              {loading ? 'Saving...' : product ? 'Update Product' : 'Create Product'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductAdminPanel;

// Category Management Modal Component
const CategoryManagementModal = ({ categories, onClose, onSuccess }) => {
  const [categoryList, setCategoryList] = useState(categories || []);
  const [newCategory, setNewCategory] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleAddCategory = async (e) => {
    e.preventDefault();
    if (!newCategory.trim()) {
      setError('Category name is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await authFetch('/api/admin/categories', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newCategory.trim()
        })
      });

      const data = await response.json();

      if (data.success) {
        // Refresh category list
        const categoriesResponse = await authFetch('/api/admin/categories');
        const categoriesData = await categoriesResponse.json();
        if (categoriesData.success) {
          setCategoryList(categoriesData.categories);
        }
        setNewCategory('');
        alert('Category added successfully!');
      } else {
        setError(data.error || 'Failed to add category');
      }
    } catch (err) {
      console.error('Error adding category:', err);
      setError('Failed to add category');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCategory = async (categoryId, categoryName) => {
    if (!window.confirm(`Delete category "${categoryName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await authFetch(`/api/admin/categories/${categoryId}`, {
        method: 'DELETE'
      });

      const data = await response.json();

      if (data.success) {
        // Refresh category list
        const categoriesResponse = await authFetch('/api/admin/categories');
        const categoriesData = await categoriesResponse.json();
        if (categoriesData.success) {
          setCategoryList(categoriesData.categories);
        }
        alert('Category deleted successfully!');
      } else {
        alert(data.error || 'Failed to delete category');
      }
    } catch (err) {
      console.error('Error deleting category:', err);
      alert('Failed to delete category');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-black text-gray-900">Manage Categories</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Add New Category Form */}
          <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-4">
            <h3 className="text-sm font-black text-purple-900 uppercase mb-3">Add New Category</h3>
            <form onSubmit={handleAddCategory} className="space-y-3">
              <div>
                <input
                  type="text"
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  placeholder="Enter category name"
                  className="w-full px-4 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  required
                />
              </div>
              {error && (
                <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded">{error}</p>
              )}
              <button
                type="submit"
                disabled={loading}
                className="w-full py-2 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Adding...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    Add Category
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Existing Categories List */}
          <div>
            <h3 className="text-sm font-black text-gray-900 uppercase mb-3">
              Existing Categories ({categoryList.length})
            </h3>
            {categoryList.length > 0 ? (
              <div className="space-y-2">
                {categoryList.map((category, index) => (
                  <div
                    key={category.id || index}
                    className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex-1">
                      <p className="font-bold text-gray-900">{category.name}</p>
                    </div>
                    <button
                      onClick={() => handleDeleteCategory(category.id, category.name)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete category"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 bg-gray-50 rounded-lg">
                <Package className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-500 text-sm">No categories yet. Add your first category above!</p>
              </div>
            )}
          </div>

          {/* Close Button */}
          <div className="flex justify-end pt-4 border-t border-gray-200">
            <button
              onClick={() => {
                onSuccess();
              }}
              className="px-6 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
